// ENG-7255: `agt probe-tools` - a one-shot, host-resident session-tool-bind
// probe for a SINGLE agent, run on demand (the operator's "Probe now" in the
// live terminal modal) rather than waiting for the manager's ~hourly cadence.
//
// Invoked machine-to-machine over SSM by POST /admin/agents/:id/probe-tools, so
// it emits a single JSON object to stdout and nothing else. It authenticates as
// the host (AGT_API_KEY -> host JWT, handled by the api client), fetches the
// agent's integrations, probes them ALL immediately (bypassing due-selection),
// persists the actionable verdicts via the same POST /host/session-tool-bind the
// manager uses, and prints the verdicts for the caller.

import { homedir } from 'node:os';
import { join } from 'node:path';
import { api } from '../lib/api-client.js';
import {
  gatherSessionToolBindProbe,
  type SessionToolBindProbeIntegration,
} from '../lib/session-tool-bind-probe-host.js';
import { readDirectChatSessionState } from '../lib/persistent-session.js';

interface ProbeToolsOptions {
  agentId?: string;
  codeName?: string;
  json?: boolean;
}

type HostIntegration = SessionToolBindProbeIntegration & { definition_id: string };

export async function probeToolsCommand(opts: ProbeToolsOptions): Promise<void> {
  const agentId = opts.agentId;
  const codeName = opts.codeName;
  const emit = (obj: unknown): void => {
    process.stdout.write(`${JSON.stringify(obj)}\n`);
  };

  if (!agentId || !codeName) {
    emit({ ok: false, error: 'agent-id and code-name are required' });
    process.exitCode = 2;
    return;
  }

  try {
    // 1. The agent's installed integrations (host-scoped; the route authorizes
    //    this host against the agent).
    const data = await api.post<{ integrations: HostIntegration[] }>('/host/agent-integrations', {
      agent_id: agentId,
    });
    const integrations = data.integrations ?? [];
    if (integrations.length === 0) {
      emit({ ok: true, agent_id: agentId, code_name: codeName, probed: 0, reported: 0, due: 0, skipped: 0, results: [] });
      return;
    }

    // 2. The MCP server keys the running session loaded at spawn. We're a
    //    separate process from the manager, so we read the persisted spawn-time
    //    snapshot (ENG-7263) rather than its in-memory session map. Absent =>
    //    empty set (conservative: pure-HTTP integrations stay 'unknown' until the
    //    session respawns and records its keys).
    const sessionLoadedServerKeys = new Set(
      readDirectChatSessionState(agentId)?.mcpServerKeys ?? [],
    );

    // 3. Probe ALL of this agent's integrations now: intervalMs 0 forces every
    //    row due; maxPerRun = the full set so nothing is deferred to a later run.
    const projectDir = join(homedir(), '.augmented', codeName, 'project');
    const result = await gatherSessionToolBindProbe(
      { agent_id: agentId, code_name: codeName },
      integrations,
      projectDir,
      { sessionLoadedServerKeys, intervalMs: 0, maxPerRun: integrations.length },
    );

    if (!result) {
      emit({
        ok: true,
        agent_id: agentId,
        code_name: codeName,
        probed: 0,
        reported: 0,
        due: 0,
        skipped: 0,
        results: [],
        note: 'no .mcp.json to probe',
      });
      return;
    }

    // 4. Persist the actionable verdicts (same path the manager uses). 'unknown'
    //    is already dropped by the runner, so a probe never regresses a row.
    if (result.reports.length > 0) {
      await api.post('/host/session-tool-bind', { agent_id: agentId, results: result.reports });
    }

    // 5. Emit the verdicts, enriched with definition_id so the caller can map
    //    them to integration rows without a second lookup.
    const defById = new Map(integrations.map((i) => [i.id, i.definition_id]));
    emit({
      ok: true,
      agent_id: agentId,
      code_name: codeName,
      probed: result.probed,
      reported: result.reports.length,
      due: result.due,
      skipped: result.skipped,
      session_loaded_server_keys: [...sessionLoadedServerKeys],
      results: result.reports.map((r) => ({
        integration_id: r.integration_id,
        definition_id: defById.get(r.integration_id) ?? null,
        scope: r.scope,
        status: r.status,
      })),
    });
  } catch (err) {
    emit({ ok: false, agent_id: agentId, code_name: codeName, error: (err as Error).message });
    process.exitCode = 1;
  }
}
